import React, { ReactNode } from 'react';
import { Box, Chip, Typography } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import PhoneIcon from '@mui/icons-material/Phone';
import { CtaButton } from '@tassomutuo-backoffice/ui';

import useDateFormat from '../../../hooks/useDateFormat';
import { ManagedLeadDetail } from '../../../services/api/managed-leads/managedLeads';
import LabelValueItem from '../../lead/components/LabelValueItem';
import { MANAGED_LEAD_PENDING_DROP_LABEL } from '../managedLeadDropFeedback';
import {
  EMPTY_MANAGED_LEAD_VALUE,
  formatManagedLeadDate,
  formatManagedLeadField,
  formatManagedLeadOwnerField,
  getManagedLeadFieldValue,
} from '../managedLeadFormatting';

interface ManagedLeadSummaryBoxProps {
  detail: ManagedLeadDetail;
  isDownloadingDocuments: boolean;
  onDownloadDocumentsPress: () => void;
  onShowNotesPress: () => void;
  onUpdatePress: () => void;
  /** The single Richiesta di aggiornamento, where the operator may send it. */
  updateRequestAction?: ReactNode;
}

/**
 * Contact block of a managed lead detail. It repeats the Mutui summary box
 * (`LeadDetailsSummaryInfoBox`) so an operator reads the same layout on every
 * product, with the data this product already exposes.
 */
const ManagedLeadSummaryBox = ({
  detail,
  isDownloadingDocuments,
  onDownloadDocumentsPress,
  onShowNotesPress,
  onUpdatePress,
  updateRequestAction,
}: ManagedLeadSummaryBoxProps) => {
  const { formatDate } = useDateFormat();
  const phone = formatManagedLeadField(detail.fields.phone);
  const hasPhone = phone !== EMPTY_MANAGED_LEAD_VALUE;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'white',
        p: 2,
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap-reverse',
            gap: 2,
            borderBottom: (th) => `1px solid ${th.palette.divider}`,
            width: '100%',
            pb: 1,
          }}
        >
          <Box display="flex" alignItems="center" flexWrap="wrap" gap={2}>
            <Typography
              fontWeight="bold"
              textTransform="uppercase"
              sx={{ color: (th) => th.palette.secondary.dark }}
            >
              {detail.stage.label}
            </Typography>
            {/* A pending Drop annotates the lead, it does not move it: the
                stage above keeps saying where the lead actually is. */}
            {detail.pendingDrop && (
              <Chip
                label={MANAGED_LEAD_PENDING_DROP_LABEL}
                color="warning"
                size="small"
              />
            )}
          </Box>
          <Box
            className="no-print"
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            {updateRequestAction}
            <CtaButton
              loading={isDownloadingDocuments}
              onClick={onDownloadDocumentsPress}
              sx={{
                bgcolor: (th) => th.palette.primary.dark,
                width: { xs: '100%', sm: 'auto' },
              }}
              variant="contained"
            >
              <DownloadIcon />
              <Typography fontWeight="bold" pl={1}>
                Scarica documenti
              </Typography>
            </CtaButton>
          </Box>
        </Box>

        <Box pt={1}>
          <Box
            display="flex"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={2}
          >
            <Box>
              <Typography
                component="h1"
                sx={{ color: (th) => th.palette.secondary.dark }}
                variant="h5"
                fontWeight="bold"
              >
                {formatManagedLeadField(detail.fields.dealName)}
              </Typography>
              <Typography variant="h6">{phone}</Typography>
            </Box>
            <LabelValueItem
              containerSx={{ justifyContent: 'flex-end' }}
              valueSx={{ textTransform: 'lowercase' }}
              value={formatManagedLeadField(detail.fields.email)}
            />
            <LabelValueItem
              containerSx={{ alignItems: { sm: 'flex-end' } }}
              label="Ultima modifica"
              value={formatManagedLeadDate(
                getManagedLeadFieldValue(detail.fields.lastModifiedAt),
                formatDate
              )}
            />
          </Box>

          <Box
            display="flex"
            justifyContent="space-between"
            flexWrap="wrap-reverse"
            gap={2}
            mt={2}
          >
            <Box display="flex" flexWrap="wrap" gap={4}>
              <LabelValueItem
                label="Responsabile commerciale"
                value={formatManagedLeadOwnerField(
                  detail.fields.salesManager,
                  'name'
                )}
              />
              <LabelValueItem
                label="Email responsabile"
                valueSx={{ textTransform: 'lowercase' }}
                value={formatManagedLeadOwnerField(
                  detail.fields.salesManager,
                  'email'
                )}
              />
            </Box>

            <LabelValueItem
              containerSx={{ alignItems: { sm: 'flex-end' } }}
              label="Creazione"
              value={formatManagedLeadDate(
                getManagedLeadFieldValue(detail.fields.createdAt),
                formatDate
              )}
            />
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          flexWrap: 'wrap',
          mt: 3,
          width: '100%',
          gap: 2,
        }}
        className="no-print"
      >
        <CtaButton
          onClick={onShowNotesPress}
          sx={{ width: { sm: 'auto', xs: '100%' } }}
        >
          <Typography variant="h6" fontWeight="bold" p={1}>
            Consulta le note
          </Typography>
        </CtaButton>
        {hasPhone && (
          <Box
            component="a"
            href={`tel:${phone}`}
            sx={{ width: { sm: 'auto', xs: '100%' } }}
          >
            <CtaButton
              variant="contained"
              sx={{ width: { sm: 'auto', xs: '100%' } }}
            >
              <PhoneIcon />
              <Typography variant="h6" fontWeight="bold" p={1}>
                Chiama
              </Typography>
            </CtaButton>
          </Box>
        )}
        {!detail.pendingDrop && (
          <Box
            sx={{
              width: { sm: '100%', xs: '100%' },
              maxWidth: '400px',
              alignSelf: 'flex-end',
            }}
          >
            <CtaButton
              sx={{
                bgcolor: (theme) => theme.palette.error.main,
                width: '100%',
                maxWidth: '400px',
              }}
              variant="contained"
              onClick={onUpdatePress}
            >
              <Typography variant="h6" fontWeight="bold" p={1}>
                Fornisci aggiornamento
              </Typography>
            </CtaButton>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ManagedLeadSummaryBox;
